import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, TrendingUp, TrendingDown, AlertTriangle, Loader2, Save } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { SEO } from "@/components/seo";
import { MainLayout } from "@/components/layout/main-layout";

interface RiskProfile {
  id: string;
  userId: string;
  riskTolerance: string;
  maxPositionSize: string;
  maxDailyLoss: string;
  maxTotalExposure: string;
  autoTradingEnabled: boolean;
  minSentimentScoreBuy: string;
  maxSentimentScoreSell: string;
  stopLossPercent: string;
  takeProfitPercent: string;
  preferredAssetClasses: string[];
}

export default function RiskSettings() {
  const { toast } = useToast();
  const [formData, setFormData] = useState<Partial<RiskProfile>>({});

  const { data: profile, isLoading } = useQuery<RiskProfile>({
    queryKey: ["/api/risk-profile"],
  });

  useEffect(() => {
    if (profile) {
      setFormData(profile);
    }
  }, [profile]);

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<RiskProfile>) => {
      const response = await apiRequest("PATCH", "/api/risk-profile", updates);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/risk-profile"] });
      toast({
        title: "Settings saved",
        description: "Your risk profile has been updated",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save risk settings",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    const { id, userId, createdAt, updatedAt, ...updates } = formData as any;
    updateMutation.mutate(updates);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Risk Settings | TradePro"
        description="Configure your risk tolerance, position limits, and automated trading thresholds"
      />
      <MainLayout
        title="Risk Management"
        subtitle="Configure your risk tolerance and automated trading thresholds"
        actions={
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            data-testid="button-save-settings"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        }
      >
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Risk Tolerance Level</CardTitle>
              <CardDescription>
                Set your overall risk appetite for trading decisions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Risk Tolerance</Label>
                <Select
                  value={formData.riskTolerance || "moderate"}
                  onValueChange={(value) => setFormData({ ...formData, riskTolerance: value })}
                >
                  <SelectTrigger data-testid="select-risk-tolerance">
                    <SelectValue placeholder="Select risk tolerance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conservative">Conservative - Lower risk, smaller positions</SelectItem>
                    <SelectItem value="moderate">Moderate - Balanced approach</SelectItem>
                    <SelectItem value="aggressive">Aggressive - Higher risk, larger positions</SelectItem>
                    <SelectItem value="very_aggressive">Very Aggressive - Maximum risk tolerance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Position Limits</CardTitle>
              <CardDescription>
                Set maximum sizes for individual positions and overall exposure
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="maxPositionSize">Max Position Size ($)</Label>
                  <Input
                    id="maxPositionSize"
                    type="number"
                    value={formData.maxPositionSize || "10000"}
                    onChange={(e) => setFormData({ ...formData, maxPositionSize: e.target.value })}
                    data-testid="input-max-position"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxDailyLoss">Max Daily Loss ($)</Label>
                  <Input
                    id="maxDailyLoss"
                    type="number"
                    value={formData.maxDailyLoss || "1000"}
                    onChange={(e) => setFormData({ ...formData, maxDailyLoss: e.target.value })}
                    data-testid="input-max-daily-loss"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxTotalExposure">Max Total Exposure ($)</Label>
                  <Input
                    id="maxTotalExposure"
                    type="number"
                    value={formData.maxTotalExposure || "100000"}
                    onChange={(e) => setFormData({ ...formData, maxTotalExposure: e.target.value })}
                    data-testid="input-max-exposure"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                Sentiment-Based Auto Trading
              </CardTitle>
              <CardDescription>
                Configure automatic buy/sell triggers based on social media sentiment scores
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Automated Trading</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow the system to execute trades based on sentiment thresholds
                  </p>
                </div>
                <Switch
                  checked={formData.autoTradingEnabled || false}
                  onCheckedChange={(checked) => setFormData({ ...formData, autoTradingEnabled: checked })}
                  data-testid="switch-auto-trading"
                />
              </div>

              {formData.autoTradingEnabled && (
                <div className="space-y-6 pt-4 border-t">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-500" />
                      <Label>Buy Signal Threshold</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Minimum sentiment score to trigger a buy order (range: -1 to 1)
                    </p>
                    <div className="flex items-center gap-4">
                      <Slider
                        value={[parseFloat(formData.minSentimentScoreBuy || "0.3") * 100]}
                        onValueChange={([value]) => setFormData({ ...formData, minSentimentScoreBuy: (value / 100).toFixed(2) })}
                        min={-100}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="w-16 text-right font-mono" data-testid="text-buy-threshold">
                        {formData.minSentimentScoreBuy || "0.30"}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="h-4 w-4 text-red-500" />
                      <Label>Sell Signal Threshold</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Maximum sentiment score to trigger a sell order (range: -1 to 1)
                    </p>
                    <div className="flex items-center gap-4">
                      <Slider
                        value={[parseFloat(formData.maxSentimentScoreSell || "-0.3") * 100]}
                        onValueChange={([value]) => setFormData({ ...formData, maxSentimentScoreSell: (value / 100).toFixed(2) })}
                        min={-100}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="w-16 text-right font-mono" data-testid="text-sell-threshold">
                        {formData.maxSentimentScoreSell || "-0.30"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                Stop Loss & Take Profit
              </CardTitle>
              <CardDescription>
                Automatic position exit thresholds
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="stopLossPercent">Stop Loss (%)</Label>
                  <Input
                    id="stopLossPercent"
                    type="number"
                    value={formData.stopLossPercent || "5"}
                    onChange={(e) => setFormData({ ...formData, stopLossPercent: e.target.value })}
                    data-testid="input-stop-loss"
                  />
                  <p className="text-xs text-muted-foreground">
                    Automatically sell when position drops by this percentage
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="takeProfitPercent">Take Profit (%)</Label>
                  <Input
                    id="takeProfitPercent"
                    type="number"
                    value={formData.takeProfitPercent || "10"}
                    onChange={(e) => setFormData({ ...formData, takeProfitPercent: e.target.value })}
                    data-testid="input-take-profit"
                  />
                  <p className="text-xs text-muted-foreground">
                    Automatically sell when position gains this percentage
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    </>
  );
}
